<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-6 max-w-[1500px] mx-auto">

    <!-- Page header -->
    <div class="flex flex-row items-end justify-between gap-4 mb-5 flex-wrap">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Returns</h1>
        <p class="text-sm text-gray-500 mt-1">Items customers are sending back - track them in, decide what happens, then put sellable stock back on the shelf</p>
      </div>
      <button
        class="group flex items-center gap-2.5 pl-2.5 pr-5 py-2 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-bold text-sm shadow-md shadow-amber-200 hover:shadow-lg hover:shadow-amber-300 hover:from-amber-300 hover:to-amber-400 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all focus:outline-none focus:ring-4 focus:ring-amber-200"
        (click)="onCreateReturn()">
        <span class="h-8 w-8 rounded-lg bg-neutral-900 text-amber-400 flex items-center justify-center transition-transform group-hover:rotate-90 duration-200">
          <mat-icon class="!text-[20px] !h-5 !w-5">add</mat-icon>
        </span>
        New return
      </button>
    </div>

    <!-- Headline figures - each is a shortcut to the returns it counts -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
      <button class="text-left bg-white rounded-2xl border shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-all" [class]="statusFilter === 'REQUESTED' ? 'border-amber-300 ring-2 ring-amber-100' : 'border-gray-100'" (click)="setStatus('REQUESTED')">
        <span class="h-11 w-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon>pending_actions</mat-icon></span>
        <div><div class="text-2xl font-bold leading-none" [class]="stats?.requested ? 'text-amber-600' : 'text-gray-900'">{{ stats?.requested ?? '–' }}</div><div class="text-xs text-gray-500 mt-1">Requested</div></div>
      </button>
      <button class="text-left bg-white rounded-2xl border shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-all" [class]="statusFilter === 'RECEIVED' ? 'border-sky-300 ring-2 ring-sky-100' : 'border-gray-100'" (click)="setStatus('RECEIVED')">
        <span class="h-11 w-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center"><mat-icon>inventory</mat-icon></span>
        <div><div class="text-2xl font-bold text-gray-900 leading-none">{{ stats?.received ?? '–' }}</div><div class="text-xs text-gray-500 mt-1">Received - to sort</div></div>
      </button>
      <button class="text-left bg-white rounded-2xl border shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-all" [class]="statusFilter === 'COMPLETED' ? 'border-emerald-300 ring-2 ring-emerald-100' : 'border-gray-100'" (click)="setStatus('COMPLETED')">
        <span class="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><mat-icon>verified</mat-icon></span>
        <div><div class="text-2xl font-bold text-gray-900 leading-none">{{ stats?.completed ?? '–' }}</div><div class="text-xs text-gray-500 mt-1">Completed</div></div>
      </button>
      <button class="text-left bg-neutral-900 rounded-2xl shadow-sm p-4 flex items-center gap-4 hover:bg-neutral-800 transition-colors" (click)="setStatus('ALL')">
        <span class="h-11 w-11 rounded-xl bg-amber-400 text-neutral-900 flex items-center justify-center"><mat-icon>assignment_return</mat-icon></span>
        <div><div class="text-2xl font-bold text-white leading-none">{{ stats?.total ?? '–' }}</div><div class="text-xs text-neutral-400 mt-1">Returns in total</div></div>
      </button>
    </div>

    @if (loadError) {
      <div class="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm flex items-center justify-between">
        <span>Could not load returns from the server.</span>
        <button class="text-xs uppercase font-semibold underline" (click)="load()">Try again</button>
      </div>
    }

    <!-- Search + filters -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 mb-5">
      <div class="relative">
        <mat-icon class="absolute left-3.5 top-1/2 -translate-y-1/2 !text-gray-400 !text-[20px] !h-5 !w-5 pointer-events-none">search</mat-icon>
        <input type="text" placeholder="Search by order number, reason or who raised it"
          class="w-full pl-11 pr-10 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm"
          name="return-search" [(ngModel)]="keyword" (ngModelChange)="onSearchInput()" />
        @if (keyword) {
        <button class="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center" (click)="clearSearch()" aria-label="Clear search">
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
        </button>
        }
      </div>
      <div class="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 flex-wrap">
        @for (o of statusOptions; track o.value) {
        <button class="flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all"
          [class]="statusFilter === o.value ? 'bg-neutral-900 text-white border-neutral-900 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:bg-amber-50'"
          (click)="setStatus(o.value)">
          {{ o.label }}
          <span class="rounded-full px-1.5 min-w-[20px] text-center text-[11px]" [class]="statusFilter === o.value ? 'bg-amber-400 text-neutral-900' : 'bg-gray-100 text-gray-500'">{{ countFor(o.value) }}</span>
        </button>
        }
        @if (filtersActive) {
        <button class="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-red-600 px-2 py-1.5 ml-auto" (click)="resetFilters()">
          <mat-icon class="!text-[16px] !h-4 !w-4">filter_alt_off</mat-icon> Clear all
        </button>
        }
      </div>
    </div>

    @if (loading && returns.length === 0) {
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm min-h-80 flex flex-col justify-center items-center">
        <mat-spinner diameter="32"></mat-spinner>
        <div class="text-sm text-gray-500 mt-3">Loading returns&hellip;</div>
      </div>
    } @else if (returns.length === 0) {
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm min-h-80 flex flex-col justify-center items-center text-center">
        <span class="h-14 w-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-3"><mat-icon class="!text-amber-500 !text-[30px] !h-[30px] !w-[30px]">assignment_return</mat-icon></span>
        <div class="text-lg font-semibold text-gray-800">{{ filtersActive ? 'No returns match these filters' : 'No returns yet' }}</div>
        <div class="text-sm text-gray-500 mt-1">{{ filtersActive ? 'Try a different search or clear the filters' : 'Start one with "New return" when a customer sends something back' }}</div>
        @if (filtersActive) { <button class="mt-4 text-sm font-semibold text-amber-700 hover:underline" (click)="resetFilters()">Clear all filters</button> }
      </div>
    } @else {
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden transition-opacity" [class.opacity-60]="loading">
        @for (row of returns; track row.id) {
        <div class="border-b border-gray-50 last:border-b-0 hover:bg-amber-50/30 transition-colors cursor-pointer" (click)="onManage(row)">
          <div class="flex items-center gap-4 px-5 py-4">

            <span class="h-11 w-11 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0"><mat-icon>assignment_return</mat-icon></span>

            <!-- What -->
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="font-semibold text-gray-900">Order #{{ row.orderNo || row.orderId }}</span>
                <span class="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full" [ngClass]="returnStatusClass(row.status)">
                  <span class="h-1.5 w-1.5 rounded-full" [class]="statusDot(row.status)"></span>{{ row.status }}
                </span>
                @if (needsRestock(row)) { <span class="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-sky-50 text-sky-700" matTooltip="Some accepted items haven't been put back into stock yet"><mat-icon class="!text-[13px] !h-[13px] !w-[13px]">inventory</mat-icon>Restock pending</span> }
              </div>
              @if (row.customerName) { <div class="text-sm text-gray-700 mt-0.5 font-medium">{{ row.customerName }}</div> }
              <div class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                <span class="inline-flex items-center gap-1"><mat-icon class="!text-[14px] !h-[14px] !w-[14px]">inventory_2</mat-icon>{{ itemSummary(row) }}</span>
                @if (row.reason) { <span class="truncate max-w-[360px]" [matTooltip]="row.reason"><mat-icon class="!text-[14px] !h-[14px] !w-[14px] align-text-bottom mr-0.5">chat_bubble_outline</mat-icon>{{ row.reason }}</span> }
              </div>
            </div>

            <!-- Items preview -->
            <div class="w-56 flex-shrink-0 hidden lg:flex flex-wrap gap-1 content-center">
              @for (item of row.items.slice(0, 2); track item.id) {
              <span class="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 truncate max-w-[200px]" [matTooltip]="(item.productName || 'Variant #' + item.productVariantId) + ' × ' + item.quantity">{{ item.quantity }}× {{ item.productName || ('#' + item.productVariantId) }}</span>
              }
              @if (row.items.length > 2) { <span class="text-[11px] text-gray-400">+{{ row.items!.length - 2 }} more</span> }
            </div>

            <!-- Who / when -->
            <div class="w-36 flex-shrink-0 hidden md:block text-right">
              <div class="text-xs font-semibold text-gray-800">{{ row.createdAt | date : 'd MMM y' }}</div>
              <div class="text-[11px] text-gray-400 mt-0.5">by {{ initiatorName(row.initiatedBy) }}</div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-1 flex-shrink-0" (click)="$event.stopPropagation()">
              <button class="h-9 w-9 rounded-full text-gray-500 hover:bg-amber-50 hover:text-amber-700 flex items-center justify-center transition-colors" (click)="onManage(row)" matTooltip="Manage return"><mat-icon class="!text-[18px]">edit_square</mat-icon></button>
            </div>
          </div>
        </div>
        }
        <mat-paginator class="border-t border-gray-100"
          [length]="count" [pageSize]="itemsPerPage" [pageIndex]="pageNumber"
          [pageSizeOptions]="[20, 50, 100]" showFirstLastButtons (page)="updateList($event)">
        </mat-paginator>
      </div>
    }

  </div>
  </div>
</app-main-layout>
